import React from 'react'
import { observer } from 'mobx-react'

import Mousetrap from 'components/mousetrap/Mousetrap'
import { IProps as ITableCellEditorParams } from './TableCellEditor'

import styles from './jsonInput.scss'

interface IProps extends ITableCellEditorParams {
  initialValue: string | null
}

interface IState {
  value: string | null
}

class JsonInput extends React.PureComponent<IProps, IState> {
  private input: React.RefObject<HTMLDivElement> = React.createRef()

  constructor(props: IProps) {
    super(props)

    this.state = {
      value: props.initialValue ?? null,
    }
  }

  // Called by ag-grid to get the final value when editing is finished
  getValue = () => this.state.value

  focus = () => {
    this.input.current?.focus()
  }

  handleChange = (stopEditingAfterChange = false) => {
    const { api, field } = this.props
    const value = this.input.current?.innerText || ''

    if (!field.isRequired && value === '') {
      // For optional json fields, it is more common to want to set the value to `null` rather than an empty string, so we're manually doing that here
      return this.setState({ value: null })
    }

    try {
      const valueAsJson = JSON.parse(value)
      if (field.isList && !Array.isArray(valueAsJson)) {
        // For Json lists, the JSON needs to be a valid array too
        throw new Error()
      }
      return this.setState(
        { value: valueAsJson },
        () => stopEditingAfterChange && api?.stopEditing()
      )
    } catch (e) {
      // If the entered value cannot be parsed as json, register the change as a (invalid) string
      return this.setState(
        { value },
        () => stopEditingAfterChange && api?.stopEditing()
      )
    }
  }

  render() {
    const { initialValue } = this.props

    return (
      <>
        <div
          data-cy="input--json"
          ref={this.input}
          contentEditable
          className={styles.input}
          dangerouslySetInnerHTML={{
            __html:
              typeof initialValue === 'string'
                ? initialValue
                : JSON.stringify(initialValue),
            // Check if the initial value is already a string.
            // This is not ideal, but when the user has deliberately entered an invalid JSON, and tries to correct it, we do not want to stringify
            // because it will add additional "" around the string
          }}
          onInput={e => this.handleChange()}
        />

        <Mousetrap
          keys="enter"
          target={this.input}
          preventDefault={false}
          onMatch={() => this.handleChange()}
        />

        <Mousetrap
          keys="cmd+enter"
          target={this.input}
          onMatch={() => this.handleChange(true)}
        />
      </>
    )
  }
}

export default observer(JsonInput)
